import React, { useRef, useState, useEffect } from "react";
import DuoRoundedIcon from "@mui/icons-material/DuoRounded";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { toast } from "react-toastify";

const MAX_VIDEO_MB = 75;
const MAX_VIDEO_SECONDS = 120;
const MAX_IMAGE_MB = 5;

const UploadVideo = ({
  video,
  setVideo,
  isBanner = false,
  isDisabled = false,
}) => {
  const fileInputRef = useRef(null);

  const [previewUrl, setPreviewUrl] = useState(null);
  const [aspectRatio, setAspectRatio] = useState(
    isBanner ? "16 / 5" : "16 / 9",
  );
  const [isBunnyEmbed, setIsBunnyEmbed] = useState(false);

  const label = isBanner ? "Banner" : "Video";
  const Icon = isBanner ? PhotoCameraIcon : DuoRoundedIcon;

  // Reset the default ratio when the mode changes
  const defaultRatio = isBanner ? "16 / 5" : "16 / 9";

  useEffect(() => {
    setAspectRatio(defaultRatio);
  }, [defaultRatio]);

  useEffect(() => {
    if (!video) {
      setPreviewUrl(null);
      setAspectRatio(defaultRatio);
      setIsBunnyEmbed(false);
      return;
    }

    // Existing URL from the server
    if (typeof video === "string") {
      const bunnyEmbed =
        !isBanner &&
        (video.includes("iframe.mediadelivery.net/embed/") ||
          video.includes("player.mediadelivery.net/embed/"));

      setIsBunnyEmbed(bunnyEmbed);

      if (bunnyEmbed) {
        const cleanedUrl = video
          .replace(/([?&])autoplay=(true|false)/gi, "")
          .replace(/([?&])preload=(true|false)/gi, "")
          .replace(/[?&]$/, "");
        const separator = cleanedUrl.includes("?") ? "&" : "?";
        setPreviewUrl(`${cleanedUrl}${separator}autoplay=false&preload=false`);
      } else {
        setPreviewUrl(video);
      }
      return;
    }

    // New File object
    const url = URL.createObjectURL(video);
    setPreviewUrl(url);
    setIsBunnyEmbed(false);

    return () => URL.revokeObjectURL(url);
  }, [video, isBanner, defaultRatio]);

  const validateImage = (file) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file only.");
      return false;
    }
    if (file.size / (1024 * 1024) > MAX_IMAGE_MB) {
      toast.error(`Please upload an image less than ${MAX_IMAGE_MB} MB`);
      return false;
    }
    return true;
  };

  const validateVideo = (file) =>
    new Promise((resolve) => {
      if (!file.type.startsWith("video/")) {
        toast.error("Please upload a video file only.");
        return resolve(false);
      }
      if (file.size / (1024 * 1024) > MAX_VIDEO_MB) {
        toast.error(`Please upload a video file less than ${MAX_VIDEO_MB} MB`);
        return resolve(false);
      }

      const vid = document.createElement("video");
      const tempUrl = URL.createObjectURL(file);
      vid.preload = "metadata";

      vid.onloadedmetadata = () => {
        URL.revokeObjectURL(tempUrl);
        if (Math.round(vid.duration) > MAX_VIDEO_SECONDS) {
          toast.error("Video duration must be 2 minutes or less");
          return resolve(false);
        }
        resolve(true);
      };

      vid.onerror = () => {
        URL.revokeObjectURL(tempUrl);
        toast.error("Failed to read the video file.");
        resolve(false);
      };

      vid.src = tempUrl;
    });

  const handleFile = async (file) => {
    if (!file) return;

    const valid = isBanner ? validateImage(file) : await validateVideo(file);
    if (!valid) return;

    setVideo(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isDisabled) return;
    handleFile(e.dataTransfer.files?.[0]);
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleInputChange = (e) => {
    handleFile(e.target.files?.[0]);
    e.target.value = ""; // allows re-selecting the same file
  };

  const openPicker = () => fileInputRef.current?.click();

  return (
    <div>
      {video && !isDisabled && (
        <div className="d-flex justify-content-end gap-2">
          <button
            type="button"
            className="f2 fs-sm-10 fs-md-12 fs-lg-13 fs-xl-14 fs-xxl-16 white push-btn bg-primary1"
            onClick={openPicker}
          >
            <Icon />
            &nbsp; Change {label}
          </button>
        </div>
      )}

      <div
        className="upload-video-container rounded-1 mt-3"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        style={{
          width: "100%",
          aspectRatio,
          maxWidth: "100%",
          borderRadius: "10px",
          overflow: "hidden",
          position: "relative",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {video ? (
          isBanner ? (
            <img
              src={previewUrl}
              alt="Banner preview"
              onLoad={(e) => {
                const { naturalWidth, naturalHeight } = e.target;
                if (naturalWidth && naturalHeight) {
                  setAspectRatio(`${naturalWidth} / ${naturalHeight}`);
                }
              }}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                display: "block",
              }}
            />
          ) : isBunnyEmbed ? (
            <iframe
              src={previewUrl}
              title="Bunny Stream Video"
              loading="lazy"
              allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                display: "block",
              }}
            />
          ) : (
            <video
              src={previewUrl}
              controls
              onLoadedMetadata={(e) => {
                const { videoWidth, videoHeight } = e.target;
                if (videoWidth && videoHeight) {
                  setAspectRatio(`${videoWidth} / ${videoHeight}`);
                }
              }}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                display: "block",
              }}
            />
          )
        ) : (
          <div className="text-center" style={{ padding: "20px" }}>
            <p>
              Choose {isBanner ? "an image" : "a video"} or drag & drop it here
            </p>
            <div className="d-flex justify-content-center gap-2 mt-2 flex-wrap">
              <button
                type="button"
                className="f2 fs-sm-10 fs-md-12 fs-lg-13 fs-xl-14 fs-xxl-16 white push-btn bg-primary1"
                onClick={openPicker}
              >
                <Icon />
                &nbsp; Upload {label}
              </button>
            </div>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept={isBanner ? "image/*" : "video/*"}
          style={{ display: "none" }}
          onChange={handleInputChange}
        />
      </div>
    </div>
  );
};

export default UploadVideo;
